/**
 * An AgentRun - AGT-001, AGT-002.
 *
 * **The run is the unit of work, and its state lives in the database.**
 * `viktor-architecture-reconstruction.md` §5.3: "The single source of truth
 * about status must not live in the process memory of an LLM worker." The
 * durable engine holds a checkpoint log that says where replay resumes; this
 * says what happened, which is a different question and the one an audit asks.
 */

/**
 * The run's lifecycle, in the order §5.3 draws it.
 *
 * `QUEUED` and not `CREATED`: the two source documents disagree, and
 * `aws/api-layer.md` §8.2 specifies the wire - `{run_id, status: "QUEUED"}` -
 * which is the half a client is compiled against. `0007_agent_run.up.sql`
 * carries the argument.
 *
 * The waiting states are separate rather than one `WAITING`, because they end
 * differently: a tool returns, an approval is decided by a person, an external
 * event may never arrive at all.
 */
export const RUN_STATUSES = [
  'QUEUED',
  'CONTEXT_BUILDING',
  'PLANNING',
  'RUNNING',
  'WAITING_FOR_TOOL',
  'WAITING_FOR_APPROVAL',
  'WAITING_FOR_EXTERNAL_EVENT',
  'RETRYING',
  'VERIFYING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'BUDGET_EXCEEDED',
  'POLICY_BLOCKED',
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

/**
 * The five states a run does not leave.
 *
 * Named once here rather than re-listed at each call site: the database has the
 * same list in `agent_run_terminal_has_end`, and two lists that must agree are
 * one list too many. `run-lifecycle.ts` in `packages/domain` reads this.
 */
export const TERMINAL_RUN_STATUSES = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'BUDGET_EXCEEDED',
  'POLICY_BLOCKED',
] as const satisfies readonly RunStatus[];

export type TerminalRunStatus = (typeof TERMINAL_RUN_STATUSES)[number];

export const isTerminalRunStatus = (status: RunStatus): status is TerminalRunStatus =>
  (TERMINAL_RUN_STATUSES as readonly RunStatus[]).includes(status);

/** What started a run. Only `CHAT` is reachable today - see the migration. */
export const RUN_TRIGGERS = ['CHAT', 'SCHEDULE', 'EVENT'] as const;
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

/**
 * Which model configuration the run uses - `agent-runtime.md` §8.3.
 *
 * A profile and not a model name, so a skill can be written once and the model
 * behind it changed without touching it. The blueprint is explicit: "Domain
 * code passes a task profile, not a model name."
 */
export const MODEL_PROFILES = ['FAST_CLASSIFIER', 'STANDARD_AGENT', 'DEEP_REASONING'] as const;
export type ModelProfile = (typeof MODEL_PROFILES)[number];

/** What a step of a run was doing. */
export const RUN_STEP_KINDS = ['CONTEXT', 'MODEL', 'TOOL', 'VERIFY', 'APPROVAL'] as const;
export type RunStepKind = (typeof RUN_STEP_KINDS)[number];

export const RUN_STEP_STATUSES = ['RUNNING', 'SUCCEEDED', 'FAILED'] as const;
export type RunStepStatus = (typeof RUN_STEP_STATUSES)[number];

/**
 * One step of the public trace.
 *
 * **`summaryRedacted` is a sentence, never a payload.** §16.4 allows this table
 * "the public execution trace, but not hidden reasoning" - so no prompt, no
 * chain of thought, and no raw tool result. When the full payload is needed it
 * lives in `tool_execution` under a stricter retention policy.
 */
export interface RunStep {
  readonly stepId: string;
  readonly sequence: number;
  readonly kind: RunStepKind;
  readonly status: RunStepStatus;
  readonly startedAt: string;
  readonly endedAt?: string;
  readonly summaryRedacted?: string;
  readonly errorCode?: string;
}

/**
 * The run as the domain sees it.
 *
 * Hand-written rather than inferred from a zod schema - this package's rule: a
 * wire shape is checked on arrival and a domain shape is not.
 *
 * **The wire half is `http/run.ts`**, and `run.spec.ts` asserts the two agree
 * in both directions, with optionality flattened away as well as kept - a type
 * missing an optional property is assignable either way, and nine of these
 * fourteen fields are optional. The only difference is deliberate:
 * `workspaceId` does not cross, because the caller named it in the header.
 */
export interface AgentRun {
  readonly id: string;
  readonly workspaceId: string;
  readonly employeeId?: string;
  readonly threadId?: string;
  readonly initiatedByUserId?: string;
  readonly trigger: RunTrigger;
  readonly triggerRef?: string;
  readonly status: RunStatus;
  /** What was asked, as it was asked. Copied, so editing an employee cannot rewrite history. */
  readonly instructionSnapshot: string;
  readonly modelProfile: ModelProfile;
  readonly createdAt: string;
  /** When the runtime picked it up. The gap from `createdAt` is queue latency. */
  readonly startedAt?: string;
  readonly completedAt?: string;
  readonly resultSummary?: string;
  readonly errorCode?: string;
}
